import { toast } from "@/hooks/use-toast";

// Default timeout for all Supabase operations (30 seconds)
const DEFAULT_TIMEOUT = 30000;

/**
 * Creates a timeout promise that rejects after the specified duration
 * @param {number} timeout - Timeout duration in milliseconds
 * @returns {Promise} Promise that rejects with timeout error
 */
function createTimeoutPromise(timeout = DEFAULT_TIMEOUT) {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error("Request timed out"));
    }, timeout);
  });
}

/**
 * Shows timeout notification and refreshes the page
 * @param {string} operation - The operation that timed out (for logging)
 * @param {boolean} shouldAutoRefresh - Whether to auto-refresh the page
 * @param {boolean} showToast - Whether to show toast notification (optional)
 */
function handleTimeout(
  operation = "operation",
  shouldAutoRefresh = true,
  showToast = true
) {
  // Use console.warn for lightweight operations, console.error for critical ones
  if (shouldAutoRefresh) {
    console.error(`🔥 Critical timeout occurred for ${operation}`);
  } else {
    console.warn(`⏱️ Lightweight timeout occurred for ${operation}`);
  }

  if (shouldAutoRefresh) {
    // Show toast notification with auto-refresh
    if (showToast) {
      toast({
        title: "Request Timed Out",
        description:
          "The request took too long to complete. The page will refresh automatically.",
        variant: "destructive",
        duration: 3000,
      });
    }

    // Auto-refresh the page after a short delay to allow user to see the toast
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  } else if (showToast) {
    // Show toast notification without auto-refresh only if explicitly requested
    toast({
      title: "Operation Timed Out",
      description:
        "Some features may not work correctly. Please try refreshing if needed.",
      variant: "destructive",
      duration: 5000,
    });
  }
}

/**
 * Wraps a Supabase RPC call with timeout handling
 * @param {Object} supabaseClient - The Supabase client instance
 * @param {string} functionName - Name of the RPC function to call
 * @param {Object} params - Parameters to pass to the RPC function
 * @param {number} timeout - Custom timeout duration (optional)
 * @param {boolean} shouldAutoRefresh - Whether to auto-refresh on timeout (optional)
 * @param {boolean} showToast - Whether to show toast on timeout (optional)
 * @returns {Promise} Promise that resolves with RPC result or rejects with timeout
 */
export async function rpcWithTimeout(
  supabaseClient,
  functionName,
  params = {},
  timeout = DEFAULT_TIMEOUT,
  shouldAutoRefresh = true,
  showToast = true
) {
  try {
    const rpcPromise = supabaseClient.rpc(functionName, params);
    const timeoutPromise = createTimeoutPromise(timeout);

    const result = await Promise.race([rpcPromise, timeoutPromise]);
    return result;
  } catch (error) {
    if (error.message === "Request timed out") {
      handleTimeout(
        `RPC function: ${functionName}`,
        shouldAutoRefresh,
        showToast
      );
      throw error;
    }
    // Re-throw non-timeout errors
    throw error;
  }
}

/**
 * Wraps a fetch call to Supabase edge functions with timeout handling
 * @param {string} url - The edge function URL
 * @param {Object} options - Fetch options (headers, body, etc.)
 * @param {number} timeout - Custom timeout duration (optional)
 * @param {boolean} shouldAutoRefresh - Whether to auto-refresh on timeout (optional)
 * @returns {Promise} Promise that resolves with fetch result or rejects with timeout
 */
export async function edgeFunctionWithTimeout(
  url,
  options = {},
  timeout = DEFAULT_TIMEOUT,
  shouldAutoRefresh = true
) {
  try {
    // Create an AbortController for the fetch request
    const controller = new AbortController();

    // Set up the timeout
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    // Add the abort signal to fetch options
    const fetchOptions = {
      ...options,
      signal: controller.signal,
    };

    const response = await fetch(url, fetchOptions);

    // Clear the timeout if request completes
    clearTimeout(timeoutId);

    return response;
  } catch (error) {
    if (error.name === "AbortError" || error.message.includes("timeout")) {
      handleTimeout(`Edge function: ${url}`, shouldAutoRefresh);
      throw new Error("Request timed out");
    }
    // Re-throw non-timeout errors
    throw error;
  }
}

/**
 * Enhanced fetch wrapper that automatically applies timeout to Supabase edge function calls
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} timeout - Custom timeout duration (optional)
 * @returns {Promise} Promise that resolves with fetch result
 */
export async function fetchWithTimeout(
  url,
  options = {},
  timeout = DEFAULT_TIMEOUT
) {
  // Check if this is a Supabase edge function call
  const isSupabaseFunction =
    url.includes("/functions/v1/") ||
    url.includes(process.env.NEXT_PUBLIC_SUPABASE_URL);

  if (isSupabaseFunction) {
    return edgeFunctionWithTimeout(url, options, timeout);
  }

  // For non-Supabase calls, use regular fetch with timeout
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    if (error.name === "AbortError") {
      console.error(`Timeout occurred for fetch: ${url}`);
      throw new Error("Request timed out");
    }
    throw error;
  }
}

/**
 * Wraps any Supabase client to automatically apply timeout to RPC calls
 * @param {Object} supabaseClient - The original Supabase client
 * @param {number} defaultTimeout - Default timeout for all operations
 * @returns {Object} Enhanced Supabase client with timeout support
 */
export function wrapSupabaseClient(
  supabaseClient,
  defaultTimeout = DEFAULT_TIMEOUT
) {
  // Don't wrap on server-side to avoid issues
  if (typeof window === "undefined") {
    return supabaseClient;
  }

  try {
    // Create a proxy that preserves all original methods and only enhances RPC
    return new Proxy(supabaseClient, {
      get(target, prop) {
        if (prop === "rpc") {
          return (functionName, params, options = {}) => {
            const timeout = options.timeout || defaultTimeout;
            const shouldAutoRefresh = options.shouldAutoRefresh !== false; // Default to true
            const showToast = options.showToast !== false; // Default to true
            return rpcWithTimeout(
              target,
              functionName,
              params,
              timeout,
              shouldAutoRefresh,
              showToast
            );
          };
        }
        // Return the original property/method for everything else
        return target[prop];
      },
    });
  } catch (error) {
    console.warn(
      "Proxy not supported, falling back to object spread with explicit method preservation"
    );

    // Fallback: Explicitly preserve key methods
    const wrappedClient = Object.create(Object.getPrototypeOf(supabaseClient));

    // Copy all enumerable properties
    Object.assign(wrappedClient, supabaseClient);

    // Ensure critical methods are preserved
    const criticalMethods = [
      "from",
      "auth",
      "storage",
      "realtime",
      "functions",
      "channel",
    ];
    criticalMethods.forEach((method) => {
      if (supabaseClient[method]) {
        wrappedClient[method] = supabaseClient[method].bind(supabaseClient);
      }
    });

    // Override RPC with timeout handling
    wrappedClient.rpc = (functionName, params, options = {}) => {
      const timeout = options.timeout || defaultTimeout;
      const shouldAutoRefresh = options.shouldAutoRefresh !== false; // Default to true
      const showToast = options.showToast !== false; // Default to true
      return rpcWithTimeout(
        supabaseClient,
        functionName,
        params,
        timeout,
        shouldAutoRefresh,
        showToast
      );
    };

    return wrappedClient;
  }
}

/**
 * Lightweight RPC call that doesn't auto-refresh on timeout
 * Ideal for quick operations like ownership checks, counts, etc.
 * @param {Object} supabaseClient - The Supabase client instance
 * @param {string} functionName - Name of the RPC function to call
 * @param {Object} params - Parameters to pass to the RPC function
 * @param {number} timeout - Custom timeout duration (default: 10s for lightweight ops)
 * @param {boolean} showToast - Whether to show toast on timeout (default: false for silent operation)
 * @returns {Promise} Promise that resolves with RPC result or rejects with timeout
 */
export async function lightweightRPC(
  supabaseClient,
  functionName,
  params = {},
  timeout = 10000, // 10 second timeout for lightweight operations
  showToast = false // Silent by default for lightweight operations
) {
  return rpcWithTimeout(
    supabaseClient,
    functionName,
    params,
    timeout,
    false,
    showToast
  );
}

/**
 * Lightweight query operation that doesn't auto-refresh on timeout
 * @param {Object} queryBuilder - Supabase query builder (e.g., supabase.from("table"))
 * @param {number} timeout - Custom timeout duration (default: 10s)
 * @returns {Promise} Promise that resolves with query result
 */
export async function lightweightQuery(
  queryBuilder,
  timeout = 10000 // 10 second timeout for lightweight operations
) {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Create a promise that rejects on timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error("Request timed out"));
      }, timeout);
    });

    // Execute the query
    const queryPromise = queryBuilder;

    const result = await Promise.race([queryPromise, timeoutPromise]);
    clearTimeout(timeoutId);

    return result;
  } catch (error) {
    if (error.message === "Request timed out") {
      handleTimeout("Lightweight query operation", false, false); // Silent timeout
      throw error;
    }
    throw error;
  }
}

// Export timeout constants for external use
export { DEFAULT_TIMEOUT };
